import {Box, Stack, Typography, IconButton, useMediaQuery, Snackbar, Alert, Button, useTheme} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import {useNavigate} from 'react-router-dom';
import {useState, useEffect, useRef} from 'react';
import {RespiratorySymptomsBase} from './base';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircleMicrophone, faCircleStop} from '@fortawesome/pro-light-svg-icons';
import React from 'react';
import ConfirmationDialog from '../../components/dialogbox/confirmationDialog';
import {isValidObjectURL} from '../../utils/miscellaneous';
import {useCoughApi} from '../../api/cough';
import {useResponsiveStyles} from '../../utils/responsiveness';

const duringRecordingColor = '#FF0000';
const afterOrBeforeRecordingColor = '#D9D9D9';
export const subtitleProps = {
  variant: 'subtitle2',
  sx: {
    marginTop: {
      xs: 2,
      md: 1,
      lg: 2,
    },
    maxWidth: '750px',
    width: '80%',
    fontSize: {xs: '14px', sm: '18px'},
    textAlign: 'center',
  },
};
const minimumRecordingDuration = 5_000; // Default duration in milliseconds
const maximumRecordingDuration = 15_000;
const coughRecordingDuration = parseInt(localStorage.getItem('coughRecordingDuration')) || minimumRecordingDuration;

/**
 * React functional component representing the Cough Symptom Checker Page.
 * @component
 * @returns {JSX.Element} The rendered CoughSymptomCheckerPage component.
 */
export const CoughSymptomCheckerPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const r = useResponsiveStyles(theme);
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const [confirmDialog, setConfirmDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
  });
  const [isRecording, setIsRecording] = useState(false);
  const [media, setMedia] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const [secondsShown, setSecondsShown] = useState(0);
  const [validationAudio, setValidationAudio] = useState(true);
  const [coughBlob, setCoughBlob] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [incorrect, setIncorrect] = useState(false);
  // track whether the media stream is ready
  const [mediaStreamReady, setMediaStreamReady] = useState(false);
  const secondsInterval = useRef(null);
  const chunks = useRef([]);

  const [isRecordedTimeCompleted, setIsRecordedTimeCompleted] = useState(false);
  const coughApi = useCoughApi();

  useEffect(() => {
    // Initialize the MediaRecorder when the component mounts
    async function initializeMediaRecorder() {
      if (!navigator.mediaDevices) {
        console.error('Navigator media devices not available');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream);

        mediaRecorder.addEventListener('dataavailable', e => {
          chunks.current.push(e.data);
        });

        setMedia(mediaRecorder);
        setIsRecording(false); // Set initial recording state to false
        setMediaStreamReady(true); // Set media stream ready
      } catch (err) {
        console.error('Error accessing microphone:', err);
      }
    }
    async function extractPossibleData() {
      const possibleSavedURL = sessionStorage.getItem('cough-value');
      if (possibleSavedURL === null || possibleSavedURL === undefined) return;

      const resourceExists = await isValidObjectURL(possibleSavedURL);
      if (!resourceExists) return;
      setIsRecording(false);
      setValidationAudio(true);
      setIsRecordedTimeCompleted(true);
      setAudioURL(possibleSavedURL);
    }

    initializeMediaRecorder();
    extractPossibleData();
  }, []);

  const startAgain = () => {
    setAudioURL('');
    chunks.current = [];
    setSecondsShown(0);
    setIsRecording(false); // Set initial recording state to false
    setIsRecordedTimeCompleted(false); // Reset the flag
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      const mediaRecorder = media;
      if (media === null || media === undefined) return;

      if (document.hidden && isRecording) {
        mediaRecorder.stop();
        clearInterval(secondsInterval.current);
        startAgain();
        setConfirmDialog({
          title: "You've been away",
          subTitle: 'Please do not minimize or change of tabs while recording the audio',
          isOpen: true,
          confirmText: 'Try again',
          noConfirmText: '',
          onConfirm: () => {
            setConfirmDialog(prev => {
              return {...prev, isOpen: false};
            });
          },
        });
        return;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (!isRecording)
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };

    secondsInterval.current = setInterval(() => {
      setSecondsShown(prev => {
        if (document.hidden) return prev;

        if (prev + 1 >= minimumRecordingDuration / 1_000) setIsRecordedTimeCompleted(true);

        if (prev + 1 === maximumRecordingDuration / 1_000) {
          stopRecording();
          setConfirmDialog({
            title: 'Audio duration is too long',
            subTitle: 'Duration of the audio exceeds the limit, select one of the options below.',
            isOpen: true,
            confirmText: 'Start again',
            noConfirmText: 'Keep the audio',
            onConfirm: () => {
              setConfirmDialog(prev => {
                return {...prev, isOpen: false};
              });
              clearInterval(secondsInterval.current);
              startAgain();
            },
          });
        }
        return prev + 1;
      });
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording]);

  const startRecording = async () => {
    if (!mediaStreamReady || !media) {
      console.error('MediaStream is not ready or media object is missing');
      return;
    }
    try {
      media.start();

      setIsRecording(true);
      setIsRecordedTimeCompleted(false); // Reset the flag
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  const stopRecording = async () => {
    if (!media || !isRecording) {
      console.error('Media object is missing or recording is not in progress');
      return;
    }
    try {
      setIsRecording(false);
      const onStop = async e => {
        if (e.data.size > 0) {
          const blob = new Blob([e.data], {type: 'audio/ogg; codecs=opus'});
          setCoughBlob(blob);
          const audioURL = URL.createObjectURL(blob);
          sessionStorage.setItem('cough-value', audioURL);
          setAudioURL(audioURL);
        }
        media.removeEventListener('dataavailable', onStop);
      };

      media.addEventListener('dataavailable', onStop);

      clearInterval(secondsInterval.current);
      media.stop();

      setValidationAudio(true);
      setErrorMessage('');
    } catch (err) {
      console.error('Error stopping recording:', err);
    }
  };

  const handleBack = () => {
    setConfirmDialog({
      title: 'Are you sure you want to cancel?',
      subTitle: 'If you cancel, you will lose your progress.',
      isOpen: true,
      onConfirm: () => {
        setConfirmDialog({
          ...confirmDialog,
          isOpen: false,
        });
        navigate('/symptom-checker');
      },
    });
  };

  const handleNext = async () => {
    if (audioURL === '') {
      setValidationAudio(false);
      setErrorMessage('You must record audio of 5 seconds to continue.');
      return;
    }

    if (!validationAudio) return;

    const formData = new FormData();
    formData.append('recording_file', coughBlob);

    coughApi
      .postCoughRecording(formData)
      .catch(error => {
        if (error.response) {
          setIncorrect(true);
          setErrorMessage('Something went wrong. Please try again.');
        }
      })
      .then(res => {
        if (res.status == 200) {
          setIncorrect(false);
          setErrorMessage('');
          navigate('/recording-confirmation-1');
        }
      });
  };

  return (
    <RespiratorySymptomsBase
      pageNumber={0}
      handleBack={() => handleBack()}
      handleNext={() => handleNext()}
      backButtonName={'Cancel'}
      backButtonIcon={''}
      nextButtonName={'Next'}
    >
      <Stack alignItems="center" mb={{sm: 0, md: 0, lg: 14}} mt={{xs: 0, md: -1, lg: 0}} className="elpepe">
        <Typography variant="h4" sx={{fontSize: isSmallScreen ? '20px' : '34px', my: 1}} textAlign="center">
          So you're having some <span style={{color: theme.palette.primary.main}}>respiratory</span> symptoms!
        </Typography>
        <Typography mt={3} {...subtitleProps}>
          Next we would like to do a respiratory health assessment to better understand what is going on. We will start
          by assessing your cough using cough sounds.
        </Typography>
        <Typography {...subtitleProps}>Please make sure you are in a quiet location! 🔇</Typography>
        <Typography fontWeight={700} {...subtitleProps}>
          To record your cough, please press the icon below and cough for 5 seconds. When you are done recording, press
          the stop button.
        </Typography>
        <Box
          fullWidth
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            mt: {xs: 2, md: 1, lg: 2},
            mb: {xs: 1, md: 0, lg: 1},
          }}
        >
          <IconButton
            color="primary"
            component="label"
            sx={{
              fontSize: {
                xs: 40,
                sm: 60,
                md: 45,
                lg: 60,
              },
            }}
            onClick={isRecordedTimeCompleted ? stopRecording : startRecording}
            disabled={isRecordedTimeCompleted ? !isRecording : isRecording}
          >
            <FontAwesomeIcon
              icon={isRecordedTimeCompleted ? faCircleStop : faCircleMicrophone}
              size={isSmallScreen ? 'xl' : '2xl'}
              style={{
                color: !isRecording ? afterOrBeforeRecordingColor : duringRecordingColor,
              }}
            />
          </IconButton>
          {isRecording ? (
            <Typography variant={subtitleProps.variant} sx={{...subtitleProps.sx, width: 'fit-content', mt: 1}}>
              {secondsShown} second(s)
              <br />
              Minimum: {Math.trunc(minimumRecordingDuration / 1_000)} second(s)
            </Typography>
          ) : (
            ''
          )}
          {audioURL && (
            <div>
              <audio
                src={audioURL}
                controls
                style={{
                  height: r({
                    xs: '45px',
                    md: '30px',
                    lg: '45px',
                  }),
                }}
              />
            </div>
          )}
          {!isRecording && isRecordedTimeCompleted ? (
            <>
              <Typography
                fontWeight={700}
                variant={subtitleProps.variant}
                sx={{
                  ...subtitleProps.sx,
                  width: '100%',
                  color: theme.palette.primary.main,
                }}
              >
                Happy with the recording? Click Next to continue
              </Typography>
              <Button onClick={startAgain} endIcon={<ReplayIcon />} sx={{my: 1}}>
                Try again
              </Button>
            </>
          ) : (
            ''
          )}
          <Snackbar
            autoHideDuration={4000}
            open={(audioURL === '' && !validationAudio) || incorrect}
            onClose={() => {
              setValidationAudio(true);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            <Alert
              severity="error"
              sx={{
                mt: 0.5,
                mb: 6,
                alignSelf: 'center',
                width: '85%', //even though is not 100% it looks like it is
                textAlign: 'center',
              }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Stack>
      {/* Confirmation dialog box when user clicks Cancel button on footer */}
      <ConfirmationDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </RespiratorySymptomsBase>
  );
};
